/**
 * Competitor-displacement ("migrate-from-X") landing-page data.
 *
 * These are BESPOKE high-intent capture pages — the NestPlot analog of
 * Casetop's /migrate-from-labstar — distinct from the spec-table /vs/{slug}
 * comparison system. They catch operators at their moment of maximum pain
 * (Storable roll-up: support collapse, price hikes, forced payment stack) and
 * offer a frictionless switch. Ad campaigns point directly here.
 *
 * Competitor claims are sourced (SoftwareConnect, Self-Storage Talk operator
 * threads) and phrased as reported/operator-reported — matching data/competitors.ts.
 * Pricing reflects the CURRENT ladder: Starter $59 flat floor (NOT the old $99).
 */

export type MigrateStep = { title: string; body: string };
export type MigratePain = { icon: string; title: string; body: string };

export interface MigrateSpec {
  slug: string; // "storedge"
  path: string; // "/migrate-from-storedge"
  competitor: string; // "storEDGE"
  vsSlug: string; // links to the /vs/{slug} spec comparison
  eyebrow: string;
  h1: string;
  sub: string;
  pricing: {
    competitor: string; // "$230–$1,000 / facility / month"
    setup: string; // "$1,000–$5,000 setup, reported"
    citation: string;
    citationLabel: string;
  };
  pains: MigratePain[];
  proof?: { quote: string; source: string };
  faqs: [string, string][];
  metaTitle: string;
  metaDescription: string;
}

export const MIGRATIONS: MigrateSpec[] = [
  {
    slug: "storedge",
    path: "/migrate-from-storedge",
    competitor: "storEDGE",
    vsSlug: "storedge",
    eyebrow: "Move off storEDGE",
    h1: "Stranded by Storable? Move off storEDGE this weekend.",
    sub: "storEDGE runs $230–$1,000 a facility, forces you onto Storable Payments, and — since the acquisition — answers support tickets in days, not hours. NestPlot is flat from $59/mo, routes tenant payments through your own Stripe (we take 0%), and migrates your roster in a single session.",
    pricing: {
      competitor: "$230–$1,000 / facility / month",
      setup: "$1,000–$5,000 setup fee, reported",
      citation: "https://softwareconnect.com/roundups/best-self-storage-software/",
      citationLabel: "SoftwareConnect roundup",
    },
    pains: [
      {
        icon: "warning",
        title: "Support collapsed after the acquisition",
        body: "Operators report tickets that disappear and week-plus response times since Storable bought storEDGE, with known bugs sitting in the engineering backlog with no ETA.",
      },
      {
        icon: "warning",
        title: "Price hikes you didn't sign up for",
        body: "Regular increases since the roll-up. Per-facility software runs $230–$1,000/mo — before the processing margin Storable takes on every tenant payment.",
      },
      {
        icon: "warning",
        title: "They own your money",
        body: "Storable Payments is non-optional — you can't bring your own processor, and their margin comes out of your rent before it ever reaches your bank.",
      },
    ],
    proof: {
      quote:
        "Since acquisition the technical support has been atrocious. Tickets get lost; DAYS to get a response.",
      source: "Self-Storage Talk operator thread",
    },
    faqs: [
      [
        "How long does it actually take to move off storEDGE?",
        "Most single-facility operators are fully live on NestPlot in a weekend. Export your tenant roster from storEDGE as a CSV, drop it into our importer, and we auto-detect the columns and let you preview every row before anything commits. On the pilot, the NestPlot team runs the whole migration with you in one 60–90 minute session.",
      ],
      [
        "Do I keep 100% of my tenant rent?",
        "Yes. NestPlot connects to your own Stripe account (Stripe Connect). Every dollar a tenant pays lands directly in your bank — NestPlot takes 0% of it, by design. We literally can't take a cut. You're no longer funding Storable's payment margin.",
      ],
      [
        "Will my gate integration still work?",
        "NestPlot ships direct integrations with PTI Falcon, SecurGuard, INSOMNIAC CIA, Noke, DaVinciLock, OpenTech IntelliGate, and Janus — so your existing access hardware keeps working after you switch.",
      ],
      [
        "What does NestPlot cost?",
        "Flat monthly, all features included: Starter $59/mo for a single facility (up to 50 units), Growth $99/mo for up to 10 properties. No per-unit metering, no setup fee, and 0% of your tenant rent. Compare that to $230–$1,000 per facility on storEDGE.",
      ],
      [
        "Is my data locked in if I ever leave NestPlot?",
        "No. Your customer data exports to CSV any time — your data is yours. We built the on-ramp to be as painless as the off-ramp precisely because storEDGE's isn't.",
      ],
    ],
    metaTitle: "Migrate from storEDGE — the $59 flat alternative that doesn't take your rent",
    metaDescription:
      "On storEDGE and squeezed by Storable? Move to NestPlot this weekend: flat from $59/mo, bring your own Stripe (0% of rent), no setup fee, CSV import from storEDGE, all-50-state lease engine built in.",
  },
  {
    slug: "sitelink",
    path: "/migrate-from-sitelink",
    competitor: "SiteLink",
    vsSlug: "sitelink",
    eyebrow: "Move off SiteLink",
    h1: "Storable is quietly starving SiteLink. Leave on your terms.",
    sub: "SiteLink still has the biggest install base in self-storage — but it runs on a Windows-era stack, sitelink.com now redirects to Storable, and operators report the roadmap has moved to Edge. Don't wait to be pushed onto Edge's pricing. NestPlot is a modern, mobile-first platform, flat from $59/mo, with your own Stripe built in.",
    pricing: {
      competitor: "$150–$1,200 / facility / month",
      setup: "$500–$1,500 setup, reported",
      citation: "https://www.selfstoragetalk.com/",
      citationLabel: "Self-Storage Talk operator threads",
    },
    pains: [
      {
        icon: "warning",
        title: "Being folded into Storable / Edge",
        body: "sitelink.com now redirects to storable.com, and operators report Storable is putting its development energy into Edge — leaving SiteLink to coast. Migrate before the migration is decided for you.",
      },
      {
        icon: "warning",
        title: "A Windows-era stack + per-user pricing",
        body: "SiteLink Web Edition is a legacy Windows product, priced per user — so your bill grows as you add staff. Custom reporting still leans on Crystal Reports know-how.",
      },
      {
        icon: "warning",
        title: "SiteLink Merchant Services takes a cut",
        body: "Payments route through Storable's processor with a margin on every transaction. NestPlot uses your own Stripe — 0% to us.",
      },
    ],
    proof: {
      quote:
        "It feels like they're moving away from development on SiteLink and putting their energy into Edge.",
      source: "Self-Storage Talk, 2026",
    },
    faqs: [
      [
        "Is SiteLink being shut down?",
        "There's no announced shut-off date — but sitelink.com now redirects to Storable, and operators report the roadmap and support attention have shifted to Edge. The risk isn't a sudden shutdown; it's slow neglect and an eventual nudge onto Edge's pricing. Moving now means you switch on your schedule, not theirs.",
      ],
      [
        "How hard is the migration from SiteLink?",
        "Straightforward. Export your roster from SiteLink to CSV and drop it into NestPlot's importer — we auto-map the columns and preview every row before committing. On the pilot, our team does the whole migration with you in one session.",
      ],
      [
        "Do I keep 100% of my tenant rent?",
        "Yes — payments run through your own Stripe Connect account and land straight in your bank. NestPlot takes 0% of tenant rent, versus the margin SiteLink Merchant Services builds into every charge.",
      ],
      [
        "What does NestPlot cost vs SiteLink?",
        "Flat, all-in: Starter $59/mo (one facility, up to 50 units), Growth $99/mo (up to 10 properties). No per-user licensing that grows with your team, no setup fee — versus $150–$1,200 per facility on SiteLink.",
      ],
    ],
    metaTitle: "Migrate from SiteLink — a modern, flat-priced alternative before Storable moves you to Edge",
    metaDescription:
      "SiteLink is being folded into Storable and its roadmap moved to Edge. Switch to NestPlot on your terms: modern, mobile-first, flat from $59/mo, your own Stripe (0% of rent), CSV import from SiteLink.",
  },
];

export function findMigration(slug: string): MigrateSpec | undefined {
  return MIGRATIONS.find((m) => m.slug === slug);
}
